/**
 * @file 用户状态管理 Store (Zustand)
 * @description 基于 Zustand 的全局用户状态管理，支持在 React 组件和非组件场景中使用
 *
 * @usage React 组件中使用:
 * ```tsx
 * import { useUserStore } from '@/store';
 *
 * const MyComponent = () => {
 *   const { userInfo, token, login, logout } = useUserStore();
 *   return <div>Welcome, {userInfo?.username}</div>;
 * };
 * ```
 *
 * @usage 非组件场景使用（如请求拦截器）:
 * ```ts
 * import { useUserStore } from '@/store';
 *
 * // 直接获取状态
 * const token = useUserStore.getState().token;
 *
 * // 直接调用方法
 * useUserStore.getState().setToken('new_token');
 * ```
 */

import { ARENA_TOKEN } from "@/utils/constants";
import Cookies from "universal-cookie";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const cookies = new Cookies();

export const selectImgCommonUrl = (state: any) => state.user.imgCommonUrl;

/**
 * 用户状态 Store
 * @description 使用 persist 中间件持久化状态到 localStorage
 * - userInfo: 持久化到 localStorage
 * - token: 持久化到 Cookie（支持 httpOnly 和跨标签页同步）
 */
/**
 * 树形菜单转平级菜单
 */
const changeTreeToFlat = (list: MenuItem[]): MenuItem[] => {
  const result: MenuItem[] = [];
  list?.forEach((item) => {
    if (item?.children) {
      const { children, ...other } = item;
      result.push(other);
      result.push(...changeTreeToFlat(children));
    } else {
      result.push(item);
    }
  });
  return result;
};

export const useUserStore = create<UserState>()(
  persist(
    (set, _get) => ({
      userInfo: null,
      token: undefined,
      userTreeMenus: [],
      userFlatMenus: [],
      permissionsList: [],
      imgCommonUrl: "",

      setToken: (token: string, days = 1) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        cookies.set(ARENA_TOKEN, token, { path: "/", expires });
        set({ token });
      },

      removeToken: () => {
        cookies.remove(ARENA_TOKEN, { path: "/" });
        set({ token: undefined });
      },

      setUserInfo: (userInfo: UserInfo) => {
        set({ userInfo });
      },

      removeUserInfo: () => {
        set({ userInfo: null });
      },

      setUserData: (data: UserDataParams) => {
        const { user, userTreeMenus = [], imgCommonUrl = "" } = data;

        // 将树形菜单转化为平级结构
        const userFlatMenus = changeTreeToFlat(userTreeMenus);
        // 提取权限编码列表并去重
        const permsList = userFlatMenus
          .map((item) => item.perms)
          .filter(Boolean) as string[];
        const permissionsList = [...new Set(permsList)];

        // 设置图片通用路径到 localStorage
        localStorage.setItem("url", imgCommonUrl);

        set({
          userInfo: user || null,
          userTreeMenus,
          userFlatMenus,
          permissionsList,
          imgCommonUrl,
        });
      },

      setUserTreeMenus: (menus: MenuItem[]) => {
        set({ userTreeMenus: menus });
      },

      setUserFlatMenus: (menus: MenuItem[]) => {
        set({ userFlatMenus: menus });
      },

      setPermissionsList: (perms: string[]) => {
        set({ permissionsList: perms });
      },

      setImgCommonUrl: (url: string) => {
        localStorage.setItem("url", url);
        set({ imgCommonUrl: url });
      },

      login: (token: string, userInfo: UserInfo, days = 1) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        cookies.set(ARENA_TOKEN, token, { path: "/", expires });
        set({ token, userInfo });
      },

      logout: () => {
        cookies.remove(ARENA_TOKEN, { path: "/" });
        localStorage.removeItem("url");
        set({
          token: undefined,
          userInfo: null,
          userTreeMenus: [],
          userFlatMenus: [],
          permissionsList: [],
          imgCommonUrl: "",
        });
      },
    }),
    {
      name: "user-storage",
      // 持久化状态到 localStorage
      partialize: (state) => ({
        userInfo: state.userInfo,
        token: state.token,
        userTreeMenus: state.userTreeMenus,
        userFlatMenus: state.userFlatMenus,
        permissionsList: state.permissionsList,
        imgCommonUrl: state.imgCommonUrl,
      }),
      // 页面加载时，从 Cookie 同步 Token（Cookie 优先级更高）
      onRehydrateStorage: () => (state) => {
        if (state) {
          const cookieToken = cookies.get(ARENA_TOKEN);
          if (cookieToken) {
            state.token = cookieToken;
          } else if (state.token) {
            // 如果 Cookie 中没有但 localStorage 有，清除（说明 Cookie 已过期）
            state.token = undefined;
            state.userInfo = null;
          }
        }
      },
    }
  )
);

export const useLoginStore = create<LoginState>()(
  persist(
    (set) => ({
      loginInfo: {},

      setLoginInfo: (info: any) => {
        set({ loginInfo: info });
      },

      removeLoginInfo: () => {
        set({ loginInfo: {} });
      },
    }),
    {
      name: "login-storage",
    }
  )
);

/**
 * 兼容旧代码的工具函数
 */
export const setTokens = (_key: string, token: string, days = 1) => {
  useUserStore.getState().setToken(token, days);
};

export const getToken = (_key: string): string | undefined => {
  return useUserStore.getState().token;
};

export const removeTokens = (_key: string) => {
  useUserStore.getState().removeToken();
};

export const clearAuth = (_key: string) => {
  useUserStore.getState().logout();
};

/**
 * 设置用户数据（用户信息、菜单、权限等）
 * @param data 用户数据参数
 */
export const setUserData = (data: UserDataParams) => {
  useUserStore.getState().setUserData(data);
};

/**
 * 获取用户平级菜单
 */
export const getUserFlatMenus = (): MenuItem[] => {
  return useUserStore.getState().userFlatMenus;
};

/**
 * 获取用户树形菜单
 */
export const getUserTreeMenus = (): MenuItem[] => {
  return useUserStore.getState().userTreeMenus;
};

/**
 * 获取权限列表
 */
export const getPermissionsList = (): string[] => {
  return useUserStore.getState().permissionsList;
};

/**
 * 获取图片通用路径
 */
export const getImgCommonUrl = (): string => {
  return useUserStore.getState().imgCommonUrl;
};

/**
 * 设置登录信息
 */
export const setLoginInfo = (info: any) => {
  useLoginStore.getState().setLoginInfo(info);
};

/**
 * 获取登录信息
 */
export const getLoginInfo = (): any => {
  return useLoginStore.getState().loginInfo;
};

/**
 * 删除登录信息
 */
export const removeLoginInfo = () => {
  useLoginStore.getState().removeLoginInfo();
};
